import { doc, getDoc, runTransaction, Timestamp } from "firebase/firestore";
import { auth, db } from "../Security/firebase-config";
import {lineupProjection, validateLineup, lineupSignature} from "./matchLineup";
import { eventMapLimit, eventSlots, flattenSetupEvent, hasSetupEventId, hasSetupMap, newSetupId, matchTitle, parseSetupKey, projectionStamp, replaceSetupEvent, resolveSetupEvent, setupKey, setupRevision, validateEventSetup } from "./eventSetup";

export function eventOwnerRef(teamId, scope = "team") {
  if (scope === "personal") {
    if (!auth.currentUser) throw new Error("Sign in first.");
    return doc(db, "users", auth.currentUser.uid);
  }
  if (!teamId) throw new Error("Select a team first.");
  return doc(db, "teams", teamId);
}
export async function readSetupSelection(teamId, key, {requireMap = false, mapId} = {}) {
  const identity = parseSetupKey(key);
  const snapshot = await getDoc(eventOwnerRef(teamId, identity?.scope));
  const event = resolveSetupEvent(snapshot.data()?.events, key);
  if (!event) throw new Error("This calendar event was removed. Choose another event or create an unplanned event.");
  const slot = eventSlots(event).find(item => item.id === (identity?.slotId || "map-1"));
  if (!slot) throw new Error("This map slot no longer exists.");
  if (requireMap && !hasSetupMap(slot)) throw new Error("Choose and save the map in Setup before uploading or recording.");
  if (requireMap && mapId !== undefined && slot.mapId !== mapId) throw new Error("The event map changed. Check the selected map in Setup before continuing.");
  if (!identity) return event;
  return flattenSetupEvent(event, slot, identity.scope);
}

// Complete only the selected slot, preserving concurrent lineup/schedule edits.
export async function saveSetupMap({teamId, key, mapId, mapName}) {
  const identity = parseSetupKey(key);
  if (!identity || !hasSetupMap({mapId, mapName})) throw new Error("Choose a map first.");
  const ref = eventOwnerRef(teamId, identity.scope);
  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(ref);
    const events = snapshot.data()?.events || [];
    const event = resolveSetupEvent(events, key);
    if (!event) throw new Error("This event was removed. Choose another event.");
    const slots = eventSlots(event);
    const slot = slots.find(item => item.id === identity.slotId);
    if (!slot) throw new Error("This map slot was removed.");
    if (hasSetupMap(slot)) {
      if (slot.mapId === mapId && (!slot.mapName || slot.mapName === mapName)) return event;
      throw new Error("Another member already selected this map. Use the saved map or edit the event Setup.");
    }
    // Existing recordings must never be silently moved to a different map path.
    if (slot.matchId || slot.localMatchId) throw new Error("This slot already has a recording. Edit the event Setup to check its map.");
    Object.assign(slot, {mapId, mapName});
    const next = validateEventSetup({...event, mapSlots: slots, lastEditedAt: Date.now(), lastEditedByUid: auth.currentUser.uid}, event);
    const updates = await projectEventSetup(transaction, teamId, next, identity.scope) || [];
    updates.forEach(([target, data]) => transaction.update(target, data));
    transaction.update(ref, {events: replaceSetupEvent(events, event, next)});
    return next;
  });
}

export async function addSetupMapSlot({teamId, key}) {
  const identity = parseSetupKey(key);
  if (!identity || !auth.currentUser) throw new Error("This event is unavailable.");
  const ref = eventOwnerRef(teamId, identity.scope);
  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(ref);
    const events = snapshot.data()?.events || [];
    const event = resolveSetupEvent(events, key);
    if (!event) throw new Error("This event was removed.");
    const slots = eventSlots(event);
    const limit = eventMapLimit(event);
    if (slots.length >= limit) throw new Error("This event already has the maximum number of maps.");
    const id = Array.from({length: 7}, (_, index) => "map-" + (index + 1))
      .find(candidate => !slots.some(slot => slot.id === candidate));
    const next = validateEventSetup({...event, mapSlots: [...slots, {id, mapId: "", mapName: ""}],
      lastEditedAt: Date.now(), lastEditedByUid: auth.currentUser.uid}, event);
    transaction.update(ref, {events: replaceSetupEvent(events, event, next)});
    return id;
  });
}

// Older calendar rows predate event IDs. Assign them once on the latest saved
// array, so concurrent edits and identical-looking events remain distinct.
export async function ensureEventSetupIds({teamId, scope = "team"}) {
  const ref = eventOwnerRef(teamId, scope);
  await runTransaction(db, async transaction => {
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error("This calendar is no longer available.");
    const events = snapshot.data()?.events;
    if (!Array.isArray(events)) return;
    let changed = false;
    const updated = events.map(event => {
      if (!event || typeof event !== "object" || Array.isArray(event) || (event.calendar || scope) !== scope || hasSetupEventId(event)) return event;
      changed = true;
      return {...event, eventId: newSetupId()};
    });
    if (changed) transaction.update(ref, {events: updated});
  });
}

// Only the workflow that created an event may remove it on cancellation. A new
// link or another member's edit wins over cleanup, even if it happens concurrently.
export async function discardUnusedSetupEvent({teamId, scope = "team", saved}) {
  if (!auth.currentUser || saved?.createdByUid !== auth.currentUser.uid) return false;
  const ref = eventOwnerRef(teamId, scope);
  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(ref);
    const events = snapshot.data()?.events || [];
    const current = events.find(event => event.eventId === saved.eventId);
    if (!current || current.createdByUid !== auth.currentUser.uid) return false;
    const linked = event => event.matchId || event.teamStatsMatchId || event.localMatchId
      || eventSlots(event).some(slot => slot.matchId || slot.statsId || slot.localMatchId);
    if (linked(current) || Object.values(current.overrides || {}).some(linked)) return false;
    const ordered = value => Array.isArray(value) ? value.map(ordered)
      : value && typeof value === "object" ? Object.fromEntries(Object.keys(value).sort().filter(key => value[key] !== undefined).map(key => [key, ordered(value[key])])) : value;
    if (JSON.stringify(ordered(current)) !== JSON.stringify(ordered(saved))) return false;
    transaction.update(ref, {events: events.filter(event => event.eventId !== saved.eventId)});
    return true;
  });
}

// Compatibility projections are written atomically with the authoritative event.
// Recording time and stats results are deliberately not schedule fields.
export async function projectEventSetup(transaction, teamId, event, scope = "team") {
  if (scope !== "team") return;
  const updates = [];
  for (const slot of eventSlots(event)) {
    const key = setupKey(event, slot.id);
    if (slot.matchId && slot.mapId) {
      const ref = doc(db, "teams", teamId, "maps", slot.mapId, "matches", slot.matchId);
      const snap = await transaction.get(ref);
      if (snap.exists()) updates.push([ref, {...lineupProjection(slot), title: matchTitle(event, slot), eventSetupRevision: projectionStamp(event, slot), calendarEventId: key,
        calendarEventTag: event.tag, calendarEventStartUTC: event.startUTC, mapName: slot.mapName || null,
        participants: event.participants || [], participantUids: event.participantsUid || []}]);
    }
    if (slot.statsId) {
      const ref = doc(db, "teams", teamId, "matches", slot.statsId);
      const snap = await transaction.get(ref);
      if (snap.exists()) updates.push([ref, {...lineupProjection(slot), title: matchTitle(event, slot), eventSetupRevision: projectionStamp(event, slot), calendarEventId: key,
        competition: event.competition || "", opponent: event.opponentName || "", map: slot.mapName || "",
        calendarEventStartUTC: event.startUTC}]);
    }
  }
  return updates;
}
export async function saveEventSetup({teamId, scope = "team", draft, previous = null, sourceTeamId = teamId, sourceScope = scope}) {
  if (previous && (sourceScope !== scope || (scope === "team" && sourceTeamId !== teamId))) {
    if (!hasSetupEventId(previous)) {
      await ensureEventSetupIds({teamId: sourceTeamId, scope: sourceScope});
      throw new Error("This older event's identity was updated. Reopen it before moving it.");
    }
    const sourceRef = eventOwnerRef(sourceTeamId, sourceScope);
    const targetRef = eventOwnerRef(teamId, scope);
    return runTransaction(db, async transaction => {
      const source = await transaction.get(sourceRef);
      const target = await transaction.get(targetRef);
      if (!source.exists() || !target.exists()) throw new Error("This calendar is no longer available.");
      const events = source.data()?.events || [];
      const current = resolveSetupEvent(events, setupKey(previous, "map-1", sourceScope));
      if (!current || setupRevision(current) !== setupRevision(previous)) throw new Error("This event changed while you were editing. Reopen it before moving it.");
      const linked = event => event.matchId || event.teamStatsMatchId || event.localMatchId || eventSlots(event).some(slot => slot.matchId || slot.statsId || slot.localMatchId);
      if (linked(current) || Object.values(current.overrides || {}).some(linked)) throw new Error("This event has linked VODs or Team Stats. Keep it in its current calendar to preserve those links.");
      const destinationEvents = target.data()?.events || [];
      if (destinationEvents.some(event => event.eventId === current.eventId)) throw new Error("This event already exists in the selected calendar.");
      const movingInstance = Boolean(current.instanceDate);
      const next = validateEventSetup({...current, ...draft, calendar: scope, participantStates: {},
        ...(movingInstance ? {eventId: newSetupId(), repeat: "none", recurrenceId: null, exceptions: [], overrides: {}, repeatEndDate: null} : {}),
        lastEditedAt: Date.now(), lastEditedByUid: auth.currentUser.uid}, current);
      delete next.__teamId;
      delete next.instanceDate;
      delete next.isRecurringInstance;
      if (scope === "personal") {delete next.participants; delete next.participantsUid; delete next.participantStates;}
      const sourceEvents = movingInstance ? events.map(event => event.eventId === current.eventId
        ? {...event, exceptions: [...new Set([...(event.exceptions || []), current.instanceDate])]} : event)
        : events.filter(event => event.eventId !== current.eventId);
      transaction.update(sourceRef, {events: sourceEvents});
      transaction.update(targetRef, {events: [...destinationEvents, next]});
      return next;
    });
  }
  // Repair identity separately from the edit so notification diffing can see an
  // unchanged legacy row gain its ID before any schedule/details are changed.
  if (previous && !hasSetupEventId(previous)) await ensureEventSetupIds({teamId, scope});
  const ref = eventOwnerRef(teamId, scope);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(ref);
    let events = snap.data()?.events || [];
    // Resolve a pre-repair editor snapshot against exactly one unchanged saved
    // source/occurrence. Ambiguous or concurrently edited rows are not guessed.
    let baseline = previous;
    if (previous && !hasSetupEventId(previous)) {
      const candidates = events.map((source, index) => {
        const eventId = source.eventId || draft.eventId || newSetupId();
        const identified = {...source, eventId};
        const selected = resolveSetupEvent([identified], setupKey({...previous, eventId}, "map-1", scope));
        return {index, identified, selected};
      }).filter(item => item.selected && setupRevision(item.selected) === setupRevision(previous)
        && (!previous.recurrenceId || item.identified.recurrenceId === previous.recurrenceId)
        && (!previous.createdAt || item.identified.createdAt === previous.createdAt));
      if (candidates.length !== 1) throw new Error("This older event could not be identified safely. Reopen the calendar event before saving.");
      const candidate = candidates[0];
      events = events.map((source, index) => index === candidate.index ? candidate.identified : source);
      baseline = {...previous, eventId: candidate.identified.eventId};
    }
    const current = resolveSetupEvent(events, setupKey(baseline || draft, "map-1", scope));
    if (previous && (!current || setupRevision(current) !== setupRevision(previous))) throw new Error("This event changed while you were editing. Your draft is preserved; reload the saved version before saving again.");
    if (!previous && current) throw new Error("This event already exists. Select it before editing.");
    // Drafts contain a full event snapshot, including data that this editor does
    // not edit (plans, participation responses, occurrence overrides). Preserve
    // current values for any field the user did not change in this draft.
    const changes = previous ? Object.fromEntries(Object.entries(draft).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(previous[key]))) : draft;
    const next = validateEventSetup({...current, ...changes, eventId: current?.eventId || draft.eventId, calendar: scope,
      createdByUid: current?.createdByUid || auth.currentUser.uid,
      createdBy: current?.createdBy || auth.currentUser.displayName || "Member",
      createdAt: current?.createdAt || new Date().toISOString(), lastEditedAt: Date.now(),
      lastEditedByUid: auth.currentUser.uid}, current);
    const updates = await projectEventSetup(transaction, teamId, next, scope) || [];
    updates.forEach(([target, data]) => transaction.update(target, data));
    transaction.update(ref, {events: current ? replaceSetupEvent(events, current, next) : [...events, next]});
    return next;
  });
}

export async function attachSetupStats({teamId, key, statsId, payload, expectedMatch}) {
  const identity = parseSetupKey(key);
  if (!identity || identity.scope !== "team") throw new Error("Save and select a team event setup first.");
  return runTransaction(db, async transaction => {
    const ref = eventOwnerRef(teamId);
    const snapshot = await transaction.get(ref);
    const events = snapshot.data()?.events || [];
    const event = resolveSetupEvent(events, key);
    if (!event) throw new Error("This event was deleted. Choose another event before saving stats.");
    const slots = eventSlots(event);
    const slot = slots.find(item => item.id === identity.slotId);
    if (!slot || (slot.statsId && slot.statsId !== statsId)) throw new Error("This map already has Team Stats. Edit that entry or choose another map slot.");
    if (expectedMatch && (slot.matchId !== expectedMatch.matchId || slot.mapId !== expectedMatch.mapId)) throw new Error("Save the selected VOD's shared setup before linking its Team Stats.");
    slot.statsId = statsId;
    const statsRef = doc(db, "teams", teamId, "matches", statsId);
    const currentStats = await transaction.get(statsRef);
    if (currentStats.exists() && currentStats.data().calendarEventId && currentStats.data().calendarEventId !== key && currentStats.data().calendarEventId !== event.eventId && resolveSetupEvent(events, currentStats.data().calendarEventId)) throw new Error("These stats are already linked to another event map.");
    const oldLinks = currentStats.data()?.linkedReviewMatches || [];
    if (!slot.matchId && oldLinks.length === 1) {
      if (slot.mapId && slot.mapId !== oldLinks[0].mapId) throw new Error("Select the original VOD's map before linking these stats.");
      slot.matchId = oldLinks[0].reviewMatchId;
      slot.mapId = oldLinks[0].mapId;
      slot.mapName = slot.mapName || currentStats.data().map || slot.mapId;
    }
    if ((currentStats.data()?.linkedReviewMatches || []).some(link => link.reviewMatchId !== slot.matchId || link.mapId !== slot.mapId)) throw new Error("These stats already belong to another VOD. Choose the stats for this map.");
    const matchRef = slot.matchId && slot.mapId ? doc(db, "teams", teamId, "maps", slot.mapId, "matches", slot.matchId) : null;
    const match = matchRef ? await transaction.get(matchRef) : null;
    if (match?.data()?.calendarEventId && match.data().calendarEventId !== key && match.data().calendarEventId !== event.eventId && resolveSetupEvent(events, match.data().calendarEventId)) throw new Error("The linked VOD belongs to another event map.");
    if (match?.data()?.teamStatsMatchId && match.data().teamStatsMatchId !== statsId) throw new Error("This VOD already has Team Stats. Edit its existing stats entry.");
    const shared = {...lineupProjection(slot), calendarEventId: key, eventSetupRevision: projectionStamp(event, slot), title: matchTitle(event, slot), competition: event.competition || "",
      opponent: event.opponentName || "", map: slot.mapName || "", calendarEventStartUTC: event.startUTC};
    if (payload) transaction.set(statsRef, {...payload, ...shared}, {merge: true});
    else if (currentStats.exists()) transaction.update(statsRef, shared);
    else throw new Error("The stats entry no longer exists.");
    if (match?.exists()) {
      const stats = {...currentStats.data(), ...payload};
      transaction.update(matchRef, {...lineupProjection(slot), calendarEventId: key, title: matchTitle(event, slot), eventSetupRevision: projectionStamp(event, slot), calendarEventTag: event.tag, calendarEventStartUTC: event.startUTC,
        teamStatsMatchId: statsId, teamStatsImageUrl: stats.imageUrl || null, teamStatsImageStoragePath: stats.imageStoragePath || null,
        linkedTeamStats: [{teamStatsMatchId: statsId, map: slot.mapName || "", dateISO: event.startUTC.slice(0, 10), score: stats.score ? `${stats.score.us ?? "?"}-${stats.score.them ?? "?"}` : ""}]});
      transaction.set(statsRef, {linkedReviewMatches: [{mapId: slot.mapId, reviewMatchId: slot.matchId, title: matchTitle(event, slot)}]}, {merge: true});
    }
    transaction.update(ref, {events: replaceSetupEvent(events, event, {...event, mapSlots: slots})});
    return statsId;
  });
}

export const setupStatsDate = event => Timestamp.fromDate(new Date(event.startUTC));

export async function saveSetupLineup({teamId, key, lineup, previous, compositionPreset}) {
  const identity = parseSetupKey(key);
  if (!identity) throw new Error("Select an event map first.");
  const ref = eventOwnerRef(teamId, identity.scope);
  return runTransaction(db, async tx => {
    const snapshot = await tx.get(ref);
    const events = snapshot.data()?.events || [];
    const event = resolveSetupEvent(events, key);
    if (!event) throw new Error("This event was removed. Select another event.");
    const slots = eventSlots(event);
    const slot = slots.find(item => item.id === identity.slotId);
    if (!slot) throw new Error("This map slot was removed.");
    if (JSON.stringify(slot.lineup || null) !== JSON.stringify(previous || null)) throw new Error("Another member changed this lineup. Cancel to load their version before editing again.");
    slot.lineup = validateLineup(lineup);
    if(compositionPreset!==undefined) {
      if(compositionPreset && (typeof compositionPreset.id!=="string" || typeof compositionPreset.name!=="string" || !["Main Comp","Secondary Comp","Custom Comp"].includes(compositionPreset.designation)))throw new Error("Choose a valid saved composition.");
      slot.compositionPreset=compositionPreset?{id:compositionPreset.id,name:compositionPreset.name,revision:String(compositionPreset.revision || ""),designation:compositionPreset.designation,lineupSignature:lineupSignature(slot.lineup)}:null;
    }
    const next = {...event, mapSlots: slots};
    // All reads precede writes; other slots and calendar edits remain intact.
    const updates = await projectEventSetup(tx, teamId, next, identity.scope) || [];
    updates.forEach(([target, data]) => tx.update(target, data));
    tx.update(ref, {events: replaceSetupEvent(events, event, next)});
  });
}

export async function removeEventSetup({teamId, scope = "team", event, future = false, mutation}) {
  const ref = eventOwnerRef(teamId, scope);
  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(ref);
    const current = snapshot.data()?.events || [];
    const matches = item => event.eventId ? item.eventId === event.eventId
      : event.recurrenceId ? item.recurrenceId === event.recurrenceId
        : item.startUTC === event.startUTC && item.endUTC === event.endUTC && item.tag === event.tag && item.createdAt === event.createdAt;
    const occurrence = !!event.instanceDate && (!!event.recurrenceId || (!!event.repeat && event.repeat !== 'none'));
    const events = current.flatMap(item => {
      if (!matches(item)) return [item];
      if (!occurrence) return [];
      if (!future) return [{...item, exceptions: [...new Set([...(item.exceptions || []), event.instanceDate])]}];
      const cutoff = new Date(`${event.instanceDate}T00:00:00Z`); cutoff.setUTCDate(cutoff.getUTCDate() - 1);
      const date = cutoff.toISOString().slice(0, 10);
      return [{...item, repeatEndDate: item.repeatEndDate && item.repeatEndDate < date ? item.repeatEndDate : date}];
    });
    transaction.update(ref, {events, ...(mutation && scope === "team" ? {lastCalendarMutation: mutation} : {})});
  });
}

export async function linkPersonalRecordingSetup({key, localMatchId, mapId, mapName}) {
  const identity = parseSetupKey(key);
  if (identity?.scope !== "personal") throw new Error("Select a personal calendar event.");
  const ref = eventOwnerRef(null, "personal");
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(ref);
    const events = snap.data()?.events || [];
    const event = resolveSetupEvent(events, key);
    if (!event) throw new Error("This personal event no longer exists.");
    const slots = eventSlots(event);
    const slot = slots.find(item => item.id === identity.slotId);
    if (!slot) throw new Error("This map slot no longer exists.");
    if (!hasSetupMap(slot)) throw new Error("Choose and save the map in Setup before recording.");
    if (slot.localMatchId && slot.localMatchId !== localMatchId) throw new Error("This personal map slot already has a local match.");
    if (mapId && slot.mapId && mapId !== slot.mapId) throw new Error("The personal event map changed. Reopen setup before recording.");
    slot.mapId = slot.mapId || mapId || ""; slot.mapName = slot.mapName || mapName || "";
    slot.localMatchId = localMatchId;
    transaction.update(ref, {events: replaceSetupEvent(events, event, {...event, mapSlots: slots})});
  });
}

// Split a series without discarding links on future occurrences or replacing a
// concurrent team-calendar edit. Overrides keep their individual recording slots.
export async function splitEventSetupSeries({teamId, scope = "team", previous, draft}) {
  if (!hasSetupEventId(previous)) await ensureEventSetupIds({teamId, scope});
  const ref = eventOwnerRef(teamId, scope);
  return runTransaction(db, async tx => {
    const snap = await tx.get(ref);
    let events = snap.data()?.events || [];
    let baseline = previous;
    if (!hasSetupEventId(previous)) {
      const candidates = events.map((source, index) => {
        const identified = {...source, eventId: source.eventId || newSetupId()};
        const selected = resolveSetupEvent([identified], setupKey({...previous,eventId:identified.eventId}, "map-1", scope));
        return {index, identified, selected};
      }).filter(item => item.identified.recurrenceId === previous.recurrenceId && item.selected && setupRevision(item.selected) === setupRevision(previous));
      if (candidates.length !== 1) throw new Error("This older series could not be identified safely. Reopen it before editing.");
      const candidate = candidates[0];
      events = events.map((source,index) => index === candidate.index ? candidate.identified : source);
      baseline = {...previous,eventId:candidate.identified.eventId};
    }
    const current = resolveSetupEvent(events, setupKey(baseline, "map-1", scope));
    if (!current || setupRevision(current) !== setupRevision(previous)) throw new Error("This occurrence changed. Reopen it before editing the series.");
    const source = events.find(event => event.eventId === baseline.eventId);
    const changes = Object.fromEntries(Object.entries(draft).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(previous[key])));
    const validated = validateEventSetup({...current, ...changes}, current);
    const splitDate = previous.instanceDate;
    const cutoff = new Date(`${splitDate}T00:00:00Z`); cutoff.setUTCDate(cutoff.getUTCDate() - 1);
    const delta = Date.parse(validated.startUTC) - Date.parse(current.startUTC);
    const shiftDate = date => new Date(Date.parse(`${date}T00:00:00Z`) + (Date.parse(validated.startUTC.slice(0, 10)) - Date.parse(current.startUTC.slice(0, 10)))).toISOString().slice(0, 10);
    const clearLinks = slot => ({...slot, matchId: "", statsId: "", localMatchId: ""});
    const next = {...source, ...validated, eventId: draft.eventId, recurrenceId: draft.eventId,
      matchId: null, teamStatsMatchId: null, mapSlots: eventSlots(validated).map(clearLinks),
      exceptions: (source.exceptions || []).filter(date => date >= splitDate).map(shiftDate), overrides: {}};
    delete next.instanceDate; delete next.isRecurringInstance; delete next.__teamId;
    for (const [date, override] of Object.entries(source.overrides || {})) {
      if (date <= splitDate) continue;
      next.overrides[shiftDate(date)] = {...override, startUTC: new Date(Date.parse(override.startUTC) + delta).toISOString(),
        endUTC: new Date(Date.parse(override.endUTC) + delta).toISOString()};
    }
    next.overrides[validated.startUTC.slice(0, 10)] = {startUTC: validated.startUTC, endUTC: validated.endUTC, mapSlots: eventSlots(validated)};
    const result = [...events.map(event => event.eventId === source.eventId ? {...event, repeatEndDate: cutoff.toISOString().slice(0, 10)} : event), next];
    tx.update(ref, {events: result});
    return result;
  });
}
